/**
 * `share-aad-encoder` — client-side mirror of the per-share AEAD canonical
 * AAD encoder per Phase B-cryptographic-mechanism Step 3b
 * (`recovery_flow_phase_b_step_3b_design.md` §2.2 v0.2 amendment per
 * [DEV3 review-and-co-sign Open Question 1 disposition](https://github.com/SmartHealthNetwork/SHN2026/pull/339#issuecomment-4405475251)).
 *
 * **Mirror source of truth:** `@shn/phg-projection-service`'s
 * `src/recovery/share-aad-encoder.ts`. This client mirror is byte-for-byte
 * parity with the server canonical by construction: both sides import the
 * same RFC 8785 JCS canonicalizer (`@shn/audit-canonical.canonicalizeForSigning`),
 * define the same 3-field AAD set, enforce the same field-value invariants,
 * and pin the same GF(2^8) shareIndex range. Drift is structurally
 * limited to field-set or invariant divergence; binding-test taxonomy at
 * `test/recovery/share-aad-encoder-binding.test.ts` parallels the server
 * binding test at
 * `services/phg-projection-service/test/recovery/share-aad-canonical-encoding-binding.test.ts`
 * for parallel-failure-on-drift signaling.
 *
 * **Why mirror not import-from-server.** `phg-projection-service` has
 * NodeJS-only AWS SDK transitive deps that would break the browser
 * bundle if imported from the client. Per `feedback_substrate_conventional_split_discipline`:
 * the encoder logic is bounded conventional convenience composing
 * against the real substrate canonical surface; the canonicalizer
 * primitive itself (`@shn/audit-canonical`) is shared verbatim. Mirror
 * approach matches PR #301 `canonical-constants.ts` + PR #326
 * `aad-canonical-encoder.ts` precedent. **Third occurrence** of
 * canonical-mirror at substrate↔client integration boundary per
 * substrate-state-warrant registry §2.5 third-occurrence elevation
 * trigger (PR #301 + PR #326 + this).
 *
 * **Per `feedback_frost_threshold_property.md`:** shared canonical
 * encoding ≠ shared cryptographic primitive. AEAD-AAD here is distinct
 * from envelope-AAD surface (`encodeRecoveryAad` per
 * [PR #323](https://github.com/SmartHealthNetwork/SHN2026/pull/323) +
 * client mirror at PR #326) and from FROST aggregate Schnorr signature
 * surface (ADR-018 §3 mode dispatch axis); only the canonical encoder
 * is shared.
 *
 * **3 required AAD fields** (per memo §2.2 v0.2 amendment):
 *   1. `subjectPciHash` — patient-identity binding
 *   2. `shareIndex` — per-share binding (1..255 GF(2^8) nonzero element
 *      space per byte-wise Shamir SSS; memo §2.3 v0.3 amendment)
 *   3. `recoveryEnvelopeId` — transcript binding to recovery envelope
 *      generation
 *
 * **Doctrinal anchors preserved** (memo §7):
 * - NO SHT BACKDOOR: encoder is stateless; SHT-held share is opaque-
 *   stored per memo §2.2 OQ3 disposition (per-share AEAD applies to the
 *   N-1 patient-controlled shares only)
 * - Recovery UX honesty: registration-time validation errors are
 *   structural (caller-bug surface); runtime AAD-mismatch surfaces as
 *   generic `EnvelopeCryptoError 'authentication-failed'` per memo §3.3
 * - Recovery ≠ erasure (ADR-024 §2.7.5): structural binding to
 *   `subjectPciHash` + `recoveryEnvelopeId` prevents cross-subject and
 *   cross-rotation-generation share reuse
 */
import { canonicalizeForSigning } from '@shn/audit-canonical';
/**
 * GF(2^8) byte-wise Shamir SSS valid share-index range. shareIndex = 0
 * is the secret position (forbidden); shareIndex > 255 is outside the
 * field. Memo §2.3 v0.3 byte-wise Shamir SSS amendment.
 */
export const SHARE_INDEX_MIN = 1;
export const SHARE_INDEX_MAX = 255;
/**
 * Encode the canonical AAD bytes for a per-share AEAD wrap. Returns
 * RFC 8785 JCS UTF-8 bytes via `@shn/audit-canonical.canonicalizeForSigning`.
 *
 * Validates field-value invariants (non-empty strings; integer share
 * index in GF(2^8) nonzero range) before canonicalization.
 *
 * @throws Error with message describing the violated invariant if any
 *   field is invalid. v1 invariants are surfaced as caller errors at
 *   registration time; recovery-flow callers MUST produce valid AAD
 *   or runtime fails closed.
 */
export function encodeShareAad(fields) {
    if (fields.subjectPciHash.length === 0) {
        throw new Error('subjectPciHash must be non-empty');
    }
    if (!Number.isInteger(fields.shareIndex)) {
        throw new Error('shareIndex must be an integer');
    }
    if (fields.shareIndex < SHARE_INDEX_MIN || fields.shareIndex > SHARE_INDEX_MAX) {
        throw new Error(`shareIndex must be in [${SHARE_INDEX_MIN}, ${SHARE_INDEX_MAX}] ` +
            `(GF(2^8) nonzero element space per byte-wise Shamir SSS)`);
    }
    if (fields.recoveryEnvelopeId.length === 0) {
        throw new Error('recoveryEnvelopeId must be non-empty');
    }
    return canonicalizeForSigning({
        recoveryEnvelopeId: fields.recoveryEnvelopeId,
        shareIndex: fields.shareIndex,
        subjectPciHash: fields.subjectPciHash,
    });
}
//# sourceMappingURL=share-aad-encoder.js.map